import React from "react";
import styled from "styled-components";
import VisibleIcon from "@rsuite/icons/Visible";
import UnvisibleIcon from "@rsuite/icons/Unvisible";
import CheckRoundIcon from "@rsuite/icons/CheckRound";
import RemindRoundIcon from "@rsuite/icons/RemindRound";
import {
  SectionRibbon,
  GlassCard,
  Badge,
  SpacedGlassCard,
} from "../../../../ui";

export const SystemSummaryFC = ({ system, acumatica }) => {
  const getSystemStatus = () => {
    const issues = [];

    if (!system.show_on_website) {
      issues.push("Hidden from clients");
    }

    if (!system.process_edu && !system.process_mag && !system.process_log) {
      issues.push("No processing enabled");
    }

    if (acumatica) {
      if (system.cus_sys_id !== acumatica.customeruniqueid)
        issues.push("CUID mismatch");
      if (system.room !== acumatica.room) issues.push("Room mismatch");
      if (system.model !== acumatica.model) issues.push("Model mismatch");
      if (system.serial_number !== acumatica.serialnumber)
        issues.push("Serial mismatch");
    } else {
      issues.push("Not in Acumatica");
    }

    return issues;
  };

  const systemIssues = getSystemStatus();
  const hasIssues = systemIssues.length > 0;

  const getProcessingStatus = () => {
    const enabled = [
      system.process_edu && "EDU",
      system.process_mag && "MAG",
      system.process_log && "LOG",
    ].filter(Boolean);

    return enabled.length > 0 ? enabled : ["None"];
  };

  const processingEnabled = getProcessingStatus();

  return (
    <SummaryContainer>
      <SectionRibbon
        title="System Overview"
        variant="primary"
        rightContent={
          <StatusBadge variant={hasIssues ? "warning" : "success"}>
            {hasIssues
              ? `${systemIssues.length} Issue${systemIssues.length > 1 ? "s" : ""}`
              : "All Good"}
          </StatusBadge>
        }
      />

      <ContentGrid>
        <InfoSection>
          <SpacedGlassCard>
            <SectionHeader>
              <SectionTitle>System Information</SectionTitle>
            </SectionHeader>

            <InfoGrid>
              <InfoItem>
                <InfoLabel>System ID</InfoLabel>
                <InfoValue highlight>{system.id}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Customer System ID</InfoLabel>
                <InfoValue>{system.cus_sys_id}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Manufacturer</InfoLabel>
                <InfoValue>{system.manufacturer}</InfoValue>
                {system.modality && (
                  <InfoSubValue>Modality: {system.modality}</InfoSubValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>Model</InfoLabel>
                <InfoValue>{system.model}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Serial Number</InfoLabel>
                <InfoValue>{system.serial_number || "N/A"}</InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Room</InfoLabel>
                <InfoValue>{system.room || "N/A"}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </SpacedGlassCard>
        </InfoSection>

        <InfoSection>
          <SpacedGlassCard>
            <SectionHeader>
              <SectionTitle>Location Information</SectionTitle>
            </SectionHeader>

            <InfoGrid>
              <InfoItem>
                <InfoLabel>Customer</InfoLabel>
                <InfoValue highlight>{system.customer_name}</InfoValue>
                <InfoSubValue>ID: {system.customer_id}</InfoSubValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Site</InfoLabel>
                <InfoValue highlight>{system.site_name}</InfoValue>
                <InfoSubValue>ID: {system.site_id}</InfoSubValue>
              </InfoItem>
            </InfoGrid>
          </SpacedGlassCard>
        </InfoSection>

        <StatusSection>
          <SpacedGlassCard>
            <SectionHeader>
              <SectionTitle>System Status</SectionTitle>
            </SectionHeader>

            <StatusGrid>
              <StatusItem>
                <StatusItemHeader>
                  <StatusIcon>
                    {system.show_on_website ? (
                      <VisibleIcon color="#27ae60" />
                    ) : (
                      <UnvisibleIcon color="#e74c3c" />
                    )}
                  </StatusIcon>
                  <StatusLabel>Client Visibility</StatusLabel>
                </StatusItemHeader>
                <StatusValue
                  variant={system.show_on_website ? "success" : "error"}
                >
                  {system.show_on_website ? "Visible" : "Hidden"}
                </StatusValue>
              </StatusItem>

              <StatusItem>
                <StatusItemHeader>
                  <StatusIcon>
                    {acumatica ? (
                      <CheckRoundIcon color="#27ae60" />
                    ) : (
                      <RemindRoundIcon color="#e74c3c" />
                    )}
                  </StatusIcon>
                  <StatusLabel>Acumatica Status</StatusLabel>
                </StatusItemHeader>
                <StatusValue variant={acumatica ? "success" : "error"}>
                  {acumatica ? "Connected" : "Not Found"}
                </StatusValue>
              </StatusItem>

              <ProcessingItem>
                <StatusItemHeader>
                  <StatusIcon>🔄</StatusIcon>
                  <StatusLabel>Processing Status</StatusLabel>
                </StatusItemHeader>
                <ProcessingBadges>
                  {processingEnabled.map((process, index) => (
                    <ProcessingBadge
                      key={index}
                      variant={process === "None" ? "error" : "success"}
                    >
                      {process}
                    </ProcessingBadge>
                  ))}
                </ProcessingBadges>
              </ProcessingItem>
            </StatusGrid>
          </SpacedGlassCard>
        </StatusSection>

        {hasIssues && (
          <IssuesSection>
            <SpacedGlassCard>
              <SectionHeader>
                <SectionIcon>⚠️</SectionIcon>
                <SectionTitle>System Issues</SectionTitle>
              </SectionHeader>

              <IssuesList>
                {systemIssues.map((issue, index) => (
                  <IssueItem key={index}>
                    <IssueIcon>•</IssueIcon>
                    <IssueText>{issue}</IssueText>
                  </IssueItem>
                ))}
              </IssuesList>

              <IssueNote>
                <NoteText>
                  Review these issues in the respective configuration sections
                  above.
                </NoteText>
              </IssueNote>
            </SpacedGlassCard>
          </IssuesSection>
        )}
      </ContentGrid>

      {acumatica && (
        <AcumaticaDetails>
          <GlassCard>
            <SectionHeader>
              <SectionTitle>Acumatica Integration Details</SectionTitle>
            </SectionHeader>

            <AcumaticaGrid>
              <AcumaticaItem>
                <AcumaticaLabel>Customer Unique ID</AcumaticaLabel>
                <AcumaticaValue
                  hasIssue={system.cus_sys_id !== acumatica.customeruniqueid}
                >
                  {acumatica.customeruniqueid}
                  {system.cus_sys_id !== acumatica.customeruniqueid && (
                    <IssueIndicator>
                      <RemindRoundIcon color="#f5a623" size="0.9em" />
                    </IssueIndicator>
                  )}
                </AcumaticaValue>
              </AcumaticaItem>

              <AcumaticaItem>
                <AcumaticaLabel>Room</AcumaticaLabel>
                <AcumaticaValue hasIssue={system.room !== acumatica.room}>
                  {acumatica.room || "N/A"}
                  {system.room !== acumatica.room && (
                    <IssueIndicator>
                      <RemindRoundIcon color="#f5a623" size="0.9em" />
                    </IssueIndicator>
                  )}
                </AcumaticaValue>
              </AcumaticaItem>

              <AcumaticaItem>
                <AcumaticaLabel>Model</AcumaticaLabel>
                <AcumaticaValue hasIssue={system.model !== acumatica.model}>
                  {acumatica.model || "N/A"}
                  {system.model !== acumatica.model && (
                    <IssueIndicator>
                      <RemindRoundIcon color="#f5a623" size="0.9em" />
                    </IssueIndicator>
                  )}
                </AcumaticaValue>
              </AcumaticaItem>

              <AcumaticaItem>
                <AcumaticaLabel>Serial Number</AcumaticaLabel>
                <AcumaticaValue
                  hasIssue={system.serial_number !== acumatica.serialnumber}
                >
                  {acumatica.serialnumber || "N/A"}
                  {system.serial_number !== acumatica.serialnumber && (
                    <IssueIndicator>
                      <RemindRoundIcon color="#f5a623" size="0.9em" />
                    </IssueIndicator>
                  )}
                </AcumaticaValue>
              </AcumaticaItem>
            </AcumaticaGrid>
          </GlassCard>
        </AcumaticaDetails>
      )}
    </SummaryContainer>
  );
};

// Styled Components
const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const StatusBadge = styled(Badge)`
  font-size: 0.85rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoSection = styled.div`
  min-height: 300px;
`;

const StatusSection = styled.div`
  grid-column: 1 / -1;

  @media (max-width: 1200px) {
    grid-column: auto;
  }
`;

const IssuesSection = styled.div`
  grid-column: 1 / -1;

  @media (max-width: 1200px) {
    grid-column: auto;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1.5rem 2rem 0 2rem;
`;

const SectionIcon = styled.div`
  font-size: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 1rem;
  padding: 0 2rem 2rem 2rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 1rem;
  font-weight: ${({ highlight }) => (highlight ? "700" : "500")};
  color: rgba(0, 0, 0, 0.9);
  font-family: ${({ highlight }) =>
    highlight ? "inherit" : "'Monaco', 'Menlo', monospace"};
`;

const InfoSubValue = styled.div`
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 0.1rem;
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  padding: 0 2rem 2rem 2rem;
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const ProcessingItem = styled(StatusItem)`
  grid-column: 1 / -1;

  @media (max-width: 768px) {
    grid-column: auto;
  }
`;

const StatusItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StatusIcon = styled.div`
  font-size: 1.2rem;
`;

const StatusLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
`;

const StatusValue = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ variant }) => {
    switch (variant) {
      case "success":
        return "#27ae60";
      case "error":
        return "#e74c3c";
      case "warning":
        return "#f5a623";
      default:
        return "rgba(0, 0, 0, 0.8)";
    }
  }};
`;

const ProcessingBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ProcessingBadge = styled(Badge)`
  font-size: 0.75rem;
`;

const IssuesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 2rem;
  margin-bottom: 1rem;
`;

const IssueItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const IssueIcon = styled.div`
  color: #f5a623;
  font-weight: bold;
`;

const IssueText = styled.div`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.8);
`;

const IssueNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem 2rem 2rem;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.85rem;
`;

const NoteIcon = styled.div`
  font-size: 1.1rem;
`;

const NoteText = styled.div`
  font-style: italic;
`;

const AcumaticaDetails = styled.div`
  margin-top: 1rem;
`;

const AcumaticaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  padding: 0 2rem 2rem 2rem;
`;

const AcumaticaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const AcumaticaLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AcumaticaValue = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.9);
  font-family: "Monaco", "Menlo", monospace;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ hasIssue }) =>
    hasIssue ? "rgba(245, 166, 35, 0.05)" : "transparent"};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  margin: -0.25rem -0.5rem;
`;

const IssueIndicator = styled.div`
  display: flex;
  align-items: center;
`;
